import React, { useRef, useState } from 'react';
import classnames from 'classnames';
import Rules from '../../utils/validation_rules';

const Input = ({
    label = '',
    placeholder = null,
    id = 'input',
    validators = [],
    errorMessage = '',
    hideOptional = true,
    defaultValue = undefined,
    value = undefined,
    required = false,
    className = '',
    type = 'text',
    hidden = false,
    readOnly = false,
    append = false,
    prepend = false,
    prependLabel = '',
    labelClass = '',
    flex = false,
    small = false,
    collapse = false,
    help = '',
    disabled = false,
    hideLabel = false,
    inputStyles = {},
    restricted = [],
    autoComplete = null,
    showRequired = false,
    onChange = () => {},
    onBlur = () => {},
    onFocus = () => {},
    onKeyUp = () => {},
    onKeyDown = () => {},
    onKeyPress = () => {}
}) => {
    const [isValid, setIsValid] = useState(true);
    const [isSuccess, setIsSuccess] = useState(false);
    const [errorText, setErrorText] = useState(null);
    const [showErrors, setShowErrors] = useState(false);
    let _input = useRef();
    const handleRestricted = (e) => {
        let restrict = restricted;

        let isClean = restrict.reduce((acc, value) => {
            return acc && !_input.current.value.includes(value);
        }, true);

        if (!isClean) {
            return;
        }

        handleChange();
    };

    const validate = () => {
        let valid = true;
        let message = errorMessage || '';
        let rules = validators;
        if (required) {
            rules = ['isRequired', ...validators];
        }

        if (type === 'email') {
            rules = [...rules, 'isEmail'];
        }

        if (!_input.current.value && !required) {
            setIsValid(valid);
            setErrorText(message);
            return valid;
        }

        rules.every((rule) => {
            let { func, msg } = Rules[rule];
            let test = func(_input.current);
            valid = valid && test;
            message = msg;
            return valid;
        });
        setErrorText(message);
        setIsValid(valid);

        return valid;
    };

    const handleFocus = () => {
        setShowErrors(!isValid);

        onFocus();
    };

    const handleChange = () => {
        if (!isValid) {
            validate();
        }
        onChange(id, _input.current.value);
    };

    const handleBlur = () => {
        if (validate()) {
            setIsSuccess(true);
        }
        setShowErrors(false);
        onBlur();
    };

    let isRequired = required || showRequired;
    let showOptional = !isRequired && !hideOptional;

    if (hidden) {
        return null;
    }

    return (
        <div className={classnames('form-group position-relative', { 'mb-0': collapse }, className)}>
            {label && !prependLabel && !hideLabel && (
                <label
                    htmlFor={id}
                    className={classnames(labelClass, {
                        required: isRequired,
                        'sr-only': hideLabel,
                        flex: flex,
                        'label-sm': small
                    })}
                >
                    {label}{' '}
                    {showOptional && <span className="text-muted font-weight-normal font-italic">(optional)</span>}
                </label>
            )}
            <div
                className={classnames({
                    'input-group': prepend || prependLabel || append
                })}
            >
                {prepend && (
                    <div className="input-group-prepend">
                        {React.isValidElement(prepend) ? prepend : <span className="input-group-text">{prepend}</span>}
                    </div>
                )}
                {prependLabel && (
                    <div
                        className={classnames('input-group-prepend prepend-label', {
                            'input-group-sm': small
                        })}
                    >
                        <label htmlFor={id} className="input-group-text">
                            {label}
                        </label>
                    </div>
                )}
                <input
                    className={classnames('form-control', {
                        error: !isValid,
                        'error-visible': !isValid && showErrors,
                        'mb-0': collapse,
                        success: isValid && isSuccess,
                        'input-group-control': prepend || prependLabel || append,
                        'input-group-control-prepend': prepend || prependLabel,
                        'input-group-control-append': append,
                        'form-control-sm': small
                    })}
                    style={inputStyles}
                    ref={_input}
                    defaultValue={defaultValue}
                    value={value}
                    type={type}
                    name={id}
                    id={id}
                    required={required}
                    placeholder={placeholder}
                    disabled={disabled}
                    autoComplete={autoComplete}
                    readOnly={readOnly}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    onChange={handleRestricted}
                    onKeyUp={onKeyUp}
                    onKeyDown={onKeyDown}
                    onKeyPress={onKeyPress}
                />
                {append && (
                    <div className="input-group-append">
                        {React.isValidElement(append) ? append : <span className="input-group-text">{append}</span>}
                    </div>
                )}
                {help && !readOnly && <small className="form-text text-muted">{help}</small>}
                {!isValid && showErrors && (
                    <div className="form-error">
                        <span>{errorText}</span>
                    </div>
                )}
            </div>
        </div>
    );
};
export default Input;
